"use client";

import {
  BellRing,
  CalendarClock,
  Clock,
  Image as ImageIcon,
  type LucideIcon,
  MessageSquare,
  MousePointerClick,
  Phone,
  Send,
  SignalHigh,
  Type,
  Zap,
} from "lucide-react";
import Image from "next/image";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";

/**
 * Shared soft backdrop that floats each focused mock. Whitespace-forward, one
 * idea per visual — never a full app-screen recreation (see scratchpad notes).
 *
 * Each visual is split into a bare `*Mock` (the product UI itself) and a
 * `*Visual` wrapper that frames it in this `MockFrame`. The alternating
 * `FeatureShowcase` uses the framed `*Visual`s; the bento variation drops the
 * bare `*Mock`s into its own card chrome.
 */
export function MockFrame({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-border bg-gradient-to-b from-muted/50 to-muted/10 p-5 sm:p-8",
        className,
      )}
    >
      <div className="relative mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}

function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-background shadow-sm", className)}>
      {children}
    </div>
  );
}

function OptInBadge({ optedIn }: { optedIn: boolean }) {
  return (
    <Badge variant="outline" className="shrink-0 bg-background font-medium">
      <span
        className={cn(
          "mr-1.5 size-1.5 shrink-0 rounded-full",
          optedIn ? "bg-success" : "bg-border",
        )}
      />
      {optedIn ? "Opted in" : "Email only"}
    </Badge>
  );
}

function SourceLine({ logo, label }: { logo: string; label: string }) {
  return (
    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Image src={logo} width={40} height={40} alt="" className="size-3.5 shrink-0 rounded-sm" />
      <span className="truncate">{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1 · Automations                                                    */
/* ------------------------------------------------------------------ */

function FlowNode({ icon: Icon, kind, title }: { icon: LucideIcon; kind: string; title: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border bg-background px-3.5 py-2.5 shadow-[0_0_0_4px_hsl(var(--brand-accent)/0.08)]">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-background">
        <Icon className="size-3.5 text-muted-foreground" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {kind}
        </p>
        <p className="truncate text-sm font-semibold text-foreground">{title}</p>
      </div>
    </div>
  );
}

function Connector({ accent }: { accent?: boolean }) {
  return (
    <div className="mx-auto h-5 w-px overflow-hidden rounded-full bg-border">
      {accent ? <div className="h-full w-full bg-[hsl(var(--brand-accent))]" /> : null}
    </div>
  );
}

export function AutomationMock() {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex size-1.5 shrink-0 rounded-full bg-success" />
        <p className="text-[13px] font-medium text-foreground">Win back lapsed members</p>
        <Badge variant="outline" className="ml-auto bg-background text-[10px]">
          Active
        </Badge>
      </div>

      <FlowNode icon={Zap} kind="Trigger" title="No visit in 21 days" />
      <Connector accent />
      <FlowNode icon={Clock} kind="Wait" title="Best time to reach" />
      <Connector accent />
      <FlowNode icon={Send} kind="Send" title="Win-back offer · SMS" />

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4">
        <div>
          <p className="text-[11px] font-medium text-muted-foreground">Conversions</p>
          <p className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">312</p>
        </div>
        <div>
          <p className="text-[11px] font-medium text-muted-foreground">Revenue</p>
          <p className="mt-0.5 text-xl font-semibold tabular-nums text-foreground">$48,200</p>
        </div>
      </div>
    </Card>
  );
}

export function AutomationVisual() {
  return (
    <MockFrame>
      <AutomationMock />
    </MockFrame>
  );
}

/* ------------------------------------------------------------------ */
/* 2 · Member data — contacts list                                    */
/* ------------------------------------------------------------------ */

type Contact = {
  name: string;
  src: string;
  source: { logo: string; label: string };
  optedIn: boolean;
};

const CONTACTS: Contact[] = [
  {
    name: "Maya Rivera",
    src: "/images/website/customers/maya-rivera.jpg",
    source: { logo: "/images/integrations/facebook/icon.png", label: "Paid ads" },
    optedIn: true,
  },
  {
    name: "Priya Nair",
    src: "/images/website/customers/priya-nair.jpg",
    source: { logo: "/images/integrations/mariana-tek/icon.png", label: "Mariana Tek" },
    optedIn: true,
  },
  {
    name: "Casey Lin",
    src: "/images/website/customers/casey-lin.jpg",
    source: { logo: "/images/icon.png", label: "Web form" },
    optedIn: true,
  },
  {
    name: "Devin Carter",
    src: "/images/website/customers/devin-carter.jpg",
    source: { logo: "/images/integrations/mindbody/icon.png", label: "Mindbody" },
    optedIn: false,
  },
];

export function ContactsMock() {
  return (
    <Card>
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <p className="text-[13px] font-semibold text-foreground">All contacts</p>
        <Badge variant="outline" className="ml-auto bg-background text-[10px]">
          2,481 members
        </Badge>
      </div>
      <div className="divide-y divide-border">
        {CONTACTS.map((c) => (
          <div key={c.name} className="flex items-center gap-3 px-4 py-3">
            <Avatar name={c.name} src={c.src} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{c.name}</p>
              <SourceLine logo={c.source.logo} label={c.source.label} />
            </div>
            <OptInBadge optedIn={c.optedIn} />
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ContactsVisual() {
  return (
    <MockFrame>
      <ContactsMock />
    </MockFrame>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · Unified inbox — member context beside the conversation         */
/* ------------------------------------------------------------------ */

function ContextRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="text-[13px] font-medium text-foreground">{value}</span>
    </div>
  );
}

export function InboxMock() {
  return (
    <Card>
      {/* Member context — what you see before you reply */}
      <div className="flex items-center gap-2.5 border-b border-border p-3.5">
        <Avatar name="Maya Rivera" src="/images/website/customers/maya-rivera.jpg" size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">Maya Rivera</p>
          <p className="truncate text-xs text-muted-foreground">Member · 14 months</p>
        </div>
        <Badge variant="outline" className="shrink-0 bg-background">
          <span className="mr-1.5 size-1.5 rounded-full bg-primary dark:bg-[hsl(var(--brand-accent))]" />
          Unlimited
        </Badge>
      </div>

      <div className="space-y-1.5 border-b border-border px-3.5 py-3">
        <ContextRow label="Last visit" value="Today" />
        <ContextRow label="Lifetime spend" value="$2,940" />
        <ContextRow label="Next class" value="Sat · 9:00 AM HIIT" />
      </div>

      {/* Conversation — the personal reply that context makes possible */}
      <div className="space-y-2 p-3.5">
        <div className="flex">
          <div className="max-w-[15rem] rounded-2xl rounded-bl-md border border-border bg-incoming-bubble px-3 py-2 text-[13px] leading-snug text-foreground">
            Do you have any openings this weekend?
          </div>
        </div>
        <div className="flex justify-end">
          <div className="max-w-[15rem] rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-[13px] leading-snug text-white shadow-sm">
            Hi Maya! Saved you a spot in Saturday's 9am HIIT. See you there 💪
          </div>
        </div>
      </div>
    </Card>
  );
}

export function InboxVisual() {
  return (
    <MockFrame>
      <InboxMock />
    </MockFrame>
  );
}

/* ------------------------------------------------------------------ */
/* 4 · Campaigns — class-fill blast with live results                 */
/* ------------------------------------------------------------------ */

function Metric({ name, value, accent }: { name: string; value: string; accent?: boolean }) {
  return (
    <div className="text-center">
      <p className="text-[11px] font-medium text-muted-foreground">{name}</p>
      <p
        className={cn(
          "mt-0.5 text-lg font-semibold tabular-nums",
          accent ? "text-success" : "text-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}

export function CampaignMock() {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-muted/50">
          <CalendarClock className="size-3.5 text-foreground" />
        </span>
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-foreground">
            Saturday class-pack promo
          </p>
          <p className="truncate text-xs text-muted-foreground">Sent to 1,204 members</p>
        </div>
        <Badge variant="outline" className="ml-auto shrink-0 bg-background text-[10px]">
          <span className="mr-1.5 size-1.5 rounded-full bg-success" />
          Sent
        </Badge>
      </div>

      <div className="flex justify-end">
        <div className="max-w-[16rem] rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-[13px] leading-snug text-white shadow-sm">
          3 spots left in Saturday's 9am HIIT. Grab a class pack and save 20%.
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 border-t border-border pt-4">
        <Metric name="Delivered" value="98%" accent />
        <Metric name="Clicks" value="32%" />
        <Metric name="Revenue" value="$5,240" />
      </div>
    </Card>
  );
}

export function CampaignVisual() {
  return (
    <MockFrame>
      <CampaignMock />
    </MockFrame>
  );
}

/* ------------------------------------------------------------------ */
/* 5 · Email builder — on-brand email, no designer                    */
/* ------------------------------------------------------------------ */

function BlockChip({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-2.5 py-1.5 shadow-sm">
      <Icon className="size-3.5 text-muted-foreground" />
      <span className="text-[11px] font-medium text-foreground">{label}</span>
    </div>
  );
}

export function EmailMock() {
  return (
    <div className="flex items-stretch gap-3">
      {/* Drag-and-drop block palette */}
      <div className="hidden shrink-0 flex-col justify-center gap-2 sm:flex">
        <BlockChip icon={Type} label="Text" />
        <BlockChip icon={ImageIcon} label="Image" />
        <BlockChip icon={MousePointerClick} label="Button" />
      </div>

      {/* The branded email being composed */}
      <Card className="min-w-0 flex-1 overflow-hidden">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
          <Image src="/images/barre3-icon.png" alt="Barre3" width={20} height={20} />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
            Barre3 West Loop
          </span>
        </div>
        <Image
          src="/images/barre3-lobby.jpg"
          alt="Barre3 Studio Lobby"
          width={800}
          height={350}
          className="aspect-[16/7] w-full object-cover"
        />

        <div className="space-y-3 p-4">
          <p className="text-sm font-bold text-foreground">Member Appreciation Week is here</p>
          <div className="space-y-1.5">
            <span className="block h-2 w-full rounded-full bg-muted" />
            <span className="block h-2 w-5/6 rounded-full bg-muted" />
            <span className="block h-2 w-3/4 rounded-full bg-muted" />
          </div>
          <button
            type="button"
            className="rounded-lg bg-outgoing-bubble px-3.5 py-2 text-xs font-semibold text-white"
          >
            Reserve my spot
          </button>
        </div>
      </Card>
    </div>
  );
}

export function EmailVisual() {
  return (
    <MockFrame>
      <EmailMock />
    </MockFrame>
  );
}

/* ------------------------------------------------------------------ */
/* 6 · iOS app — incoming call with member context                    */
/* ------------------------------------------------------------------ */

/**
 * Reusable device chrome, mirroring the in-app phone frame (`MessagePreview`)
 * so it inherits the same light/dark treatment (`dark:invert` on the frame art,
 * theme-aware body fill). Renders a single full-bleed screen slot — no app
 * header — so it can host a takeover screen like an incoming call.
 */
function PhoneShell({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("relative mx-auto aspect-[366/729] w-full max-w-[260px]", className)}>
      <div className="absolute inset-y-[calc(1/729*100%)] left-[calc(7/366*100%)] right-[calc(5/366*100%)] rounded-[calc(58/366*100%)/calc(58/729*100%)] shadow-2xl" />
      <div className="absolute left-[calc(23/366*100%)] top-[calc(23/729*100%)] h-[calc(686/729*100%)] w-[calc(318/366*100%)] overflow-hidden bg-background">
        {children}
      </div>
      <svg
        viewBox="0 0 366 729"
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full text-muted-foreground"
      >
        <path
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M300.092 1c41.22 0 63.223 21.99 63.223 63.213V184.94c-.173.184-.329.476-.458.851.188-.282.404-.547.647-.791.844-.073 2.496.257 2.496 2.157V268.719c-.406 2.023-2.605 2.023-2.605 2.023a7.119 7.119 0 0 1-.08-.102v394.462c0 41.213-22.001 63.212-63.223 63.212h-95.074c-.881-.468-2.474-.795-4.323-.838l-33.704-.005-.049.001h-.231l-.141-.001c-2.028 0-3.798.339-4.745.843H66.751c-41.223 0-63.223-21.995-63.223-63.208V287.739c-.402-.024-2.165-.23-2.524-2.02v-.973A2.039 2.039 0 0 1 1 284.62v-47.611c0-.042.001-.084.004-.126v-.726c0-1.9 1.652-2.23 2.496-2.157l.028.028v-16.289c-.402-.024-2.165-.23-2.524-2.02v-.973A2.039 2.039 0 0 1 1 214.62v-47.611c0-.042.001-.084.004-.126v-.726c0-1.9 1.652-2.23 2.496-2.157l.028.028v-26.041a2.26 2.26 0 0 0 .093-.236l-.064-.01a3.337 3.337 0 0 1-.72-.12l-.166-.028A2 2 0 0 1 1 135.62v-24.611a2 2 0 0 1 1.671-1.973l.857-.143v-44.68C3.528 22.99 25.53 1 66.75 1h233.341ZM3.952 234.516a5.481 5.481 0 0 0-.229-.278c.082.071.159.163.228.278Zm89.99-206.304A4.213 4.213 0 0 0 89.727 24H56.864C38.714 24 24 38.708 24 56.852v618.296C24 693.292 38.714 708 56.864 708h250.272c18.15 0 32.864-14.708 32.864-32.852V56.852C340 38.708 325.286 24 307.136 24h-32.864a4.212 4.212 0 0 0-4.213 4.212v2.527c0 10.235-8.3 18.532-18.539 18.532H112.48c-10.239 0-18.539-8.297-18.539-18.532v-2.527Z"
        />
        <rect x="154" y="29" width="56" height="5" rx="2.5" fill="currentColor" opacity="0.6" />
      </svg>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        alt=""
        aria-hidden="true"
        width={366}
        height={729}
        decoding="async"
        className="pointer-events-none absolute inset-0 h-full w-full dark:invert"
        src="/images/phone-frame.svg"
      />
    </div>
  );
}

function CallContextRow({
  icon: Icon,
  logo,
  children,
}: {
  icon?: LucideIcon;
  logo?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex size-5 shrink-0 items-center justify-center">
        {logo ? (
          <Image src={logo} width={32} height={32} alt="" className="size-4 rounded-[4px]" />
        ) : Icon ? (
          <Icon className="size-3.5 text-zinc-500 dark:text-zinc-400" />
        ) : null}
      </span>
      <span className="truncate text-[11px] leading-tight text-zinc-700 dark:text-zinc-100">
        {children}
      </span>
    </div>
  );
}

function CallSecondary({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="flex size-9 items-center justify-center rounded-full bg-zinc-900/[0.06] text-zinc-700 dark:bg-white/10 dark:text-zinc-100">
        <Icon className="size-4" />
      </span>
      <span className="text-[10px] font-medium text-zinc-500 dark:text-zinc-400">{label}</span>
    </div>
  );
}

function CallAction({ kind }: { kind: "accept" | "decline" }) {
  const accept = kind === "accept";
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span
        className={cn(
          "flex size-12 items-center justify-center rounded-full text-white shadow-lg",
          accept ? "bg-green-500" : "bg-red-500",
        )}
      >
        <Phone className={cn("size-5", !accept && "rotate-[135deg]")} fill="currentColor" />
      </span>
      <span className="text-[10px] font-medium text-zinc-600 dark:text-zinc-300">
        {accept ? "Accept" : "Decline"}
      </span>
    </div>
  );
}

/**
 * iOS-style status-bar battery: a hairline body with a solid fill whose width
 * tracks the charge level (no segmented bars), plus the small terminal cap.
 */
function BatteryIcon({ level = 0.9, className }: { level?: number; className?: string }) {
  const clamped = Math.max(0, Math.min(1, level));
  return (
    <svg
      viewBox="0 0 25 12"
      fill="none"
      aria-hidden="true"
      className={cn("h-[11px] w-[23px]", className)}
    >
      <rect
        x="0.5"
        y="0.5"
        width="21"
        height="11"
        rx="3"
        stroke="currentColor"
        strokeOpacity="0.4"
      />
      <rect x="23" y="4" width="1.6" height="4" rx="0.8" fill="currentColor" fillOpacity="0.4" />
      <rect x="2" y="2" width={clamped * 18} height="8" rx="1.5" fill="currentColor" />
    </svg>
  );
}

/**
 * Theme-aware call takeover: a clean light screen in light mode and a true
 * iOS-style dark takeover in dark mode. The surrounding device frame flips with
 * the theme too (white in light, black in dark).
 */
export function IncomingCallMock({ className }: { className?: string }) {
  return (
    <PhoneShell className={className}>
      <div className="flex h-full flex-col bg-gradient-to-b from-white via-zinc-50 to-zinc-100 px-4 pb-4 pt-[6%] text-zinc-900 dark:from-zinc-800 dark:via-zinc-900 dark:to-zinc-950 dark:text-white">
        {/* Status bar — sits in the safe area beside the speaker */}
        <div className="flex items-center justify-between px-0.5 text-[10px] font-semibold">
          <span className="tabular-nums">9:41</span>
          <div className="flex items-center gap-1">
            <SignalHigh className="size-3" />
            <BatteryIcon />
          </div>
        </div>

        {/* App + call state */}
        <div className="mt-4 flex items-center justify-center gap-1.5">
          <Image
            src="/images/icon.png"
            width={32}
            height={32}
            alt=""
            className="size-4 rounded-[5px]"
          />
          <span className="text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
            Incoming call
          </span>
        </div>

        {/* Caller */}
        <div className="mt-3 flex flex-col items-center text-center">
          <Image
            src="/images/website/customers/maya-rivera.jpg"
            alt="Maya Rivera"
            width={160}
            height={160}
            className="size-14 rounded-full object-cover shadow-lg ring-2 ring-black/5 dark:ring-white/10"
          />
          <p className="mt-2.5 text-[15px] font-semibold">Maya Rivera</p>
        </div>

        {/* The extra context — what makes the call worth answering */}
        <div className="mt-3.5 space-y-2 rounded-2xl border border-zinc-900/[0.08] bg-background p-2.5 dark:border-white/10">
          <CallContextRow icon={CalendarClock}>Tour booked · 2h ago</CallContextRow>
          <CallContextRow logo="/images/integrations/facebook/icon.png">
            From Paid ads
          </CallContextRow>
        </div>

        <div className="flex-1" />

        {/* Secondary actions */}
        <div className="flex items-center justify-center gap-10">
          <CallSecondary icon={BellRing} label="Remind" />
          <CallSecondary icon={MessageSquare} label="Message" />
        </div>

        {/* Accept / decline */}
        <div className="mt-4 flex items-end justify-between px-2">
          <CallAction kind="decline" />
          <CallAction kind="accept" />
        </div>

        {/* Home indicator */}
        <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-zinc-900/20 dark:bg-white/30" />
      </div>
    </PhoneShell>
  );
}

export function IncomingCallVisual() {
  return (
    <MockFrame>
      <IncomingCallMock className="max-w-[248px]" />
    </MockFrame>
  );
}
